import React from "react";
import { motionSafe as motion } from "@/lib/motionSafe";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Layers3,
  Mail,
  Phone,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { SEO } from "../components/site/SEO";
import { SERVICES_CONTENT } from "../data/servicesContent";
import { BOOKING_URL } from "../lib/booking";

const CONTACT_HREF = "/#contact";
const SITE_URL = "https://nextech.law";

// Content is visible from the very first render (opacity stays 1); the subtle
// y-offset motion is decorative only and never gates visibility. Under
// prefers-reduced-motion, MotionConfig (App root) disables the transform.
const fade = {
  initial: { opacity: 1, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const SectionEyebrow = ({ children }) => (
  <div className="font-mono-tech text-xs uppercase tracking-[0.26em] text-[#3B82F6]">{children}</div>
);

const ActionLink = ({ href, children, external = false, variant = "primary", testId }) => {
  const className = variant === "primary"
    ? "trace-btn inline-flex min-h-11 items-center justify-center gap-2 bg-[#0052FF] hover:bg-[#2563EB] text-white text-sm font-semibold px-5 py-3 rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 focus:ring-offset-[#030712]"
    : "inline-flex min-h-11 items-center justify-center gap-2 border border-white/15 hover:border-[#3B82F6]/60 hover:bg-[#0052FF]/10 text-white text-sm font-semibold px-5 py-3 rounded-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-offset-2 focus:ring-offset-[#030712]";
  if (external) return <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={className}>{children} <ArrowRight size={15} /></a>;
  if (href.startsWith("/")) return <Link to={href} data-testid={testId} className={className}>{children} <ArrowRight size={15} /></Link>;
  return <a href={href} data-testid={testId} className={className}>{children} <ArrowRight size={15} /></a>;
};

const BulletList = ({ items, icon = true }) => (
  <ul className="space-y-3">
    {items.map((item) => (
      <li key={item} className="flex gap-3 text-base leading-relaxed text-gray-300">
        {icon && <CheckCircle2 size={17} className="mt-1 shrink-0 text-[#3B82F6]" />}
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

function OverviewCard({ card, index, lang }) {
  const href = card.href || `#${card.anchor}`;
  const firstLabel = index === 2 ? (lang === "de" ? "Hinweis:" : "Note:") : (lang === "de" ? "Geeignet für:" : "Best for:");
  const secondLabel = index === 2 ? (lang === "de" ? "Zusatz:" : "Additional note:") : (lang === "de" ? "Ergebnis:" : "Outcome:");
  return (
    <motion.article
      {...fade}
      data-testid={`services-card-${card.anchor || "cra-control-room"}`}
      className={`group relative overflow-hidden rounded-sm border p-6 md:p-7 transition-colors ${
        card.featured
          ? "border-[#0052FF]/55 bg-[#0052FF]/10 shadow-[0_0_70px_rgba(0,82,255,0.16)]"
          : "border-white/10 bg-[#07111F] hover:border-[#3B82F6]/45"
      }`}
    >
      {card.featured && <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-[#0052FF]/20 blur-3xl" />}
      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <span className="font-mono-tech text-xs text-[#3B82F6]">0{index + 1}</span>
          {card.badge && (
            <span className={`rounded-sm border px-2.5 py-1 font-mono-tech text-[10px] uppercase tracking-[0.16em] ${card.featured ? "border-[#60A5FA]/50 bg-[#0052FF]/20 text-[#BFDBFE]" : "border-[#F59E0B]/35 bg-[#F59E0B]/10 text-[#FBBF24]"}`}>
              {card.badge}
            </span>
          )}
        </div>
        <h3 className="mt-5 font-display text-2xl font-extrabold text-white">{card.name}</h3>
        <div className="mt-3 text-lg font-semibold text-[#93C5FD]">{card.price}</div>
        {card.priceNote && <p className="mt-1 text-sm text-gray-400">{card.priceNote}</p>}
        <p className="mt-4 text-base leading-relaxed text-gray-300">{card.short}</p>
        <div className="mt-5 space-y-4 border-t border-white/10 pt-5">
          <p className="text-sm leading-relaxed text-gray-400"><span className="font-semibold text-gray-200">{firstLabel}</span> {card.bestFor}</p>
          <p className="text-sm leading-relaxed text-gray-400"><span className="font-semibold text-gray-200">{secondLabel}</span> {card.outcome}</p>
        </div>
        <ActionLink href={href} variant={card.featured ? "primary" : "secondary"} testId={`services-card-cta-${index + 1}`}>{card.cta}</ActionLink>
      </div>
    </motion.article>
  );
}

function DetailShell({ id, eyebrow, title, subtitle, featured = false, badge, children, testId }) {
  return (
    <motion.section
      {...fade}
      id={id}
      aria-labelledby={`${id}-heading`}
      data-testid={testId}
      className={`scroll-mt-28 rounded-sm border p-6 md:p-10 ${
        featured ? "border-[#0052FF]/45 bg-[#0052FF]/8" : "border-white/10 bg-[#07111F]"
      }`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <SectionEyebrow>{eyebrow}</SectionEyebrow>
        {badge && <span className="rounded-sm border border-[#F59E0B]/35 bg-[#F59E0B]/10 px-2.5 py-1 font-mono-tech text-[10px] uppercase tracking-[0.16em] text-[#FBBF24]">{badge}</span>}
      </div>
      <h2 id={`${id}-heading`} className="mt-4 font-display text-3xl md:text-5xl font-extrabold tracking-tight text-white">{title}</h2>
      <p className="mt-4 max-w-3xl text-lg md:text-xl leading-relaxed text-gray-300">{subtitle}</p>
      <div className="mt-8">{children}</div>
    </motion.section>
  );
}

export default function ServicesPage() {
  // Language is determined by the URL, never by stored/browser preference:
  // /services is always German, /en/services is always English.
  const { pathname } = useLocation();
  const lang = pathname.startsWith("/en/") ? "en" : "de";
  const c = SERVICES_CONTENT[lang] || SERVICES_CONTENT.de;
  const pagePath = lang === "en" ? "/en/services" : "/services";
  const pageUrl = `${SITE_URL}${pagePath}`;

  const serviceNode = ({ id, name, serviceType, description, offers }) => ({
    "@type": "Service",
    "@id": `${SITE_URL}/services#service-${id}`,
    name,
    serviceType,
    provider: { "@id": `${SITE_URL}/#organization` },
    areaServed: "European Union",
    url: `${pageUrl}#${id}`,
    ...(description ? { description } : {}),
    ...(offers ? { offers } : {}),
  });

  const jsonLd = [
    serviceNode({
      id: "cra-entry-check",
      name: "CRA Entry Check",
      serviceType: "Cyber Resilience Act product classification",
      offers: { "@type": "Offer", price: "990", priceCurrency: "EUR", url: `${pageUrl}#cra-entry-check` },
    }),
    serviceNode({
      id: "cra-mobilisation-sprint",
      name: "CRA Mobilisation Sprint",
      serviceType: "Cyber Resilience Act implementation mobilisation",
      offers: { "@type": "Offer", price: "6900", priceCurrency: "EUR", url: `${pageUrl}#cra-mobilisation-sprint` },
    }),
    // CRA Control Room: announced as COMING SOON — deliberately NO offers and
    // no availability claim anywhere (visible text and schema must match).
    serviceNode({
      id: "cra-control-room",
      name: "CRA Control Room",
      serviceType: "Digital CRA readiness workspace",
      description: lang === "de" ? "Geplante digitale Arbeitsumgebung; derzeit nicht verfügbar und nicht kaufbar." : "Planned digital workspace; currently not available and not purchasable.",
    }),
    serviceNode({
      id: "cra-as-a-service",
      name: "CRA-as-a-Service",
      serviceType: "Ongoing CRA programme leadership",
    }),
    {
      "@type": "FAQPage",
      inLanguage: lang,
      mainEntity: c.faq.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  ];

  return (
    <div data-testid="services-page" className="min-h-screen bg-[#030712] text-white overflow-x-hidden">
      <SEO
        path={pagePath}
        lang={lang}
        title={c.seo.title}
        description={c.seo.description}
        alternates={[
          { hreflang: "de", href: `${SITE_URL}/services` },
          { hreflang: "en", href: `${SITE_URL}/en/services` },
          { hreflang: "x-default", href: `${SITE_URL}/services` },
        ]}
        breadcrumbs={[{ name: lang === "de" ? "Leistungen" : "Services", path: pagePath }]}
        jsonLd={jsonLd}
        type="website"
      />
      <Header />
      <main>
        <header className="relative pt-32 md:pt-40 pb-16 md:pb-24">
          <div className="absolute top-0 left-1/2 h-96 w-[720px] -translate-x-1/2 rounded-full bg-[#0052FF]/10 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-6 md:px-10">
            <motion.div {...fade}>
              <SectionEyebrow>{c.hero.eyebrow}</SectionEyebrow>
              <h1 data-testid="services-title" className="mt-5 max-w-5xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] text-white">{c.hero.title}</h1>
              <p className="mt-6 max-w-3xl text-lg md:text-xl leading-relaxed text-gray-300">{c.hero.intro}</p>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-gray-400">{c.hero.additional}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <ActionLink href="#services-overview" testId="services-hero-primary">{c.hero.primary}</ActionLink>
                <ActionLink href={BOOKING_URL} external variant="secondary" testId="services-hero-booking"><Calendar size={16} /> {c.hero.secondary}</ActionLink>
              </div>
            </motion.div>
          </div>
        </header>

        <motion.section {...fade} data-testid="services-free-checker" aria-labelledby="services-free-checker-heading" className="max-w-7xl mx-auto px-6 md:px-10 pb-10">
          <div className="rounded-sm border border-[#3B82F6]/25 bg-[#07111F] p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 rounded-sm border border-[#10B981]/30 bg-[#10B981]/10 px-3 py-1 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#6EE7B7]"><Sparkles size={13} /> {lang === "de" ? "Kostenloser Orientierungsschritt" : "Free orientation step"}</div>
                <h2 id="services-free-checker-heading" className="mt-4 font-display text-2xl md:text-3xl font-extrabold text-white">{c.checker.title}</h2>
                <p className="mt-3 text-base leading-relaxed text-gray-300">{c.checker.body}</p>
                <p className="mt-3 text-sm leading-relaxed text-gray-400">{c.checker.note}</p>
              </div>
              <ActionLink href="/#assessment" variant="secondary" testId="services-free-checker-cta">{c.checker.cta}</ActionLink>
            </div>
          </div>
        </motion.section>

        <section id="services-overview" data-testid="services-overview" aria-labelledby="services-overview-heading" className="scroll-mt-28 max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-16">
          <motion.div {...fade} className="max-w-3xl">
            <SectionEyebrow>{c.overview.label}</SectionEyebrow>
            <h2 id="services-overview-heading" className="mt-4 font-display text-3xl md:text-5xl font-extrabold tracking-tight text-white">{c.overview.title}</h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-400">{c.overview.intro}</p>
          </motion.div>
          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            {c.overview.cards.map((card, index) => <OverviewCard key={card.name} card={card} index={index} lang={lang} />)}
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 md:px-10 space-y-8 md:space-y-10 py-10">
          <DetailShell id="cra-entry-check" testId="services-detail-entry" eyebrow={c.entry.eyebrow} title={c.entry.title} subtitle={c.entry.subtitle}>
            <p className="max-w-3xl text-base leading-relaxed text-gray-300">{c.entry.intro}</p>
            <div className="mt-8 grid gap-8 lg:grid-cols-3">
              <div><h3 className="font-display text-xl font-bold text-white">{c.entry.questionsTitle}</h3><div className="mt-4"><BulletList items={c.entry.questions} /></div></div>
              <div><h3 className="font-display text-xl font-bold text-white">{c.entry.includedTitle}</h3><div className="mt-4"><BulletList items={c.entry.included} /></div></div>
              <div><h3 className="font-display text-xl font-bold text-white">{c.entry.excludedTitle}</h3><div className="mt-4"><BulletList items={c.entry.excluded} /></div></div>
            </div>
            <div className="mt-8 rounded-sm border border-[#3B82F6]/25 bg-[#0052FF]/5 p-5">
              <div className="text-lg font-semibold text-white">{c.entry.price}</div>
              <p className="mt-3 text-sm leading-relaxed text-gray-400">{c.entry.disclaimer}</p>
              <div className="mt-5"><ActionLink href={CONTACT_HREF} testId="services-entry-request">{c.entry.cta}</ActionLink></div>
            </div>
          </DetailShell>

          <DetailShell id="cra-mobilisation-sprint" testId="services-detail-sprint" eyebrow={c.sprint.eyebrow} title={c.sprint.title} subtitle={c.sprint.subtitle} featured>
            <p className="max-w-4xl text-base md:text-lg leading-relaxed text-gray-300">{c.sprint.intro}</p>
            <div className="mt-8 grid gap-8 xl:grid-cols-2">
              <div><h3 className="font-display text-xl font-bold text-white">{c.sprint.bestForTitle}</h3><div className="mt-4"><BulletList items={c.sprint.bestFor} /></div></div>
              <div><h3 className="font-display text-xl font-bold text-white">{c.sprint.includesTitle}</h3><div className="mt-4"><BulletList items={c.sprint.included} /></div></div>
            </div>
            <div className="mt-10">
              <h3 className="font-display text-xl font-bold text-white">{c.sprint.processTitle}</h3>
              <div className="mt-5 grid gap-4 md:grid-cols-3">
                {c.sprint.process.map((step, index) => (
                  <div key={step.title} className="rounded-sm border border-white/10 bg-white/[0.03] p-5">
                    <div className="font-mono-tech text-xs text-[#3B82F6]">0{index + 1}</div>
                    <h4 className="mt-3 font-display text-lg font-bold text-white">{step.title}</h4>
                    <p className="mt-3 text-sm leading-relaxed text-gray-400">{step.body}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-10 rounded-sm border border-[#10B981]/30 bg-[#10B981]/5 p-5">
              <h3 className="font-display text-xl font-bold text-white">{c.sprint.outcomeTitle}</h3>
              <p className="mt-3 text-base leading-relaxed text-gray-300">{c.sprint.outcome}</p>
            </div>
            <div className="mt-8 grid gap-8 lg:grid-cols-2">
              <div><h3 className="font-display text-xl font-bold text-white">{c.sprint.excludedTitle}</h3><div className="mt-4"><BulletList items={c.sprint.excluded} /></div></div>
              <div className="rounded-sm border border-[#0052FF]/35 bg-[#0052FF]/10 p-5">
                <div className="text-xl font-semibold text-white">{c.sprint.price}</div>
                <p className="mt-3 text-sm text-gray-300">{c.sprint.scope}</p>
                <p className="mt-3 text-sm leading-relaxed text-gray-400">{c.sprint.definition}</p>
                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                  <ActionLink href={BOOKING_URL} external testId="services-sprint-booking">{c.sprint.primary}</ActionLink>
                  <ActionLink href={CONTACT_HREF} variant="secondary" testId="services-sprint-contact">{c.sprint.secondary}</ActionLink>
                </div>
              </div>
            </div>
          </DetailShell>

          <DetailShell id="cra-control-room" testId="services-detail-control" eyebrow={c.control.eyebrow} title={c.control.title} subtitle={c.control.subtitle} badge={c.control.badge}>
            <div className="rounded-sm border border-[#F59E0B]/35 bg-[#F59E0B]/10 p-5">
              <div data-testid="services-control-price" className="font-display text-2xl md:text-3xl font-extrabold text-white">{c.control.price}</div>
              <p className="mt-3 text-base leading-relaxed text-gray-300">{c.control.body}</p>
              <p className="mt-3 text-base leading-relaxed text-gray-300">{c.control.additional}</p>
            </div>
            <div className="mt-8 grid gap-8 lg:grid-cols-2">
              <div><h3 className="font-display text-xl font-bold text-white">{c.control.featuresTitle}</h3><div className="mt-4"><BulletList items={c.control.features} /></div></div>
              <div><h3 className="font-display text-xl font-bold text-white">{c.control.pricingTitle}</h3><div className="mt-4"><BulletList items={c.control.pricing} /></div></div>
            </div>
            <p className="mt-6 text-sm leading-relaxed text-gray-400">{c.control.disclaimer}</p>
            <div className="mt-6"><ActionLink href="/cra-control-room" variant="secondary" testId="services-control-more"><ExternalLink size={15} /> {c.control.cta}</ActionLink></div>
          </DetailShell>

          <span id="cra-implementation" aria-hidden="true" className="block h-0 scroll-mt-28" />
          <DetailShell id="cra-as-a-service" testId="services-detail-caas" eyebrow={c.caas.eyebrow} title={c.caas.title} subtitle={c.caas.subtitle}>
            <p className="max-w-4xl text-base md:text-lg leading-relaxed text-gray-300">{c.caas.intro}</p>
            <div className="mt-8 grid gap-8 lg:grid-cols-2">
              <div><h3 className="font-display text-xl font-bold text-white">{c.caas.workstreamsTitle}</h3><div className="mt-4"><BulletList items={c.caas.workstreams} /></div></div>
              <div className="space-y-6">
                <div><h3 className="font-display text-xl font-bold text-white">{c.caas.deliveryTitle}</h3><p className="mt-4 text-base leading-relaxed text-gray-300">{c.caas.delivery}</p></div>
                <div className="rounded-sm border border-[#3B82F6]/25 bg-[#0052FF]/5 p-5"><p className="text-base leading-relaxed text-gray-300">{c.caas.note}</p><div className="mt-4 text-lg font-semibold text-white">{c.caas.price}</div><div className="mt-5"><ActionLink href={BOOKING_URL} external testId="services-caas-booking">{c.caas.cta}</ActionLink></div></div>
              </div>
            </div>
          </DetailShell>
        </div>

        <section data-testid="services-process" aria-labelledby="services-process-heading" className="max-w-7xl mx-auto px-6 md:px-10 py-14">
          <motion.div {...fade}>
            <SectionEyebrow>{c.process.eyebrow}</SectionEyebrow>
            <h2 id="services-process-heading" className="mt-4 font-display text-3xl md:text-5xl font-extrabold tracking-tight text-white">{c.process.title}</h2>
            <div className="mt-10 grid gap-4 md:grid-cols-4">
              {c.process.steps.map((step, index) => (
                <div key={step.title} className="rounded-sm border border-white/10 bg-[#07111F] p-5">
                  <div className="font-mono-tech text-xs text-[#3B82F6]">0{index + 1}</div>
                  <h3 className="mt-3 font-display text-lg font-bold text-white">{step.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-400">{step.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 rounded-sm border border-[#F59E0B]/30 bg-[#F59E0B]/8 p-4 text-sm md:text-base text-gray-300"><Clock3 size={16} className="mr-2 inline text-[#FBBF24]" />{c.process.note}</div>
          </motion.div>
        </section>

        <section data-testid="services-why" aria-labelledby="services-why-heading" className="max-w-7xl mx-auto px-6 md:px-10 py-14">
          <motion.div {...fade} className="rounded-sm border border-white/10 bg-[#07111F] p-6 md:p-10">
            <SectionEyebrow>{c.why.eyebrow}</SectionEyebrow>
            <h2 id="services-why-heading" className="mt-4 max-w-4xl font-display text-3xl md:text-5xl font-extrabold tracking-tight text-white">{c.why.title}</h2>
            <p className="mt-5 max-w-3xl text-lg leading-relaxed text-gray-300">{c.why.body}</p>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {c.why.pillars.map((pillar) => (
                <div key={pillar.title} className="rounded-sm border border-white/10 bg-white/[0.025] p-5">
                  <Layers3 size={18} className="text-[#3B82F6]" />
                  <h3 className="mt-4 font-display text-lg font-bold text-white">{pillar.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-400">{pillar.body}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 border-t border-white/10 pt-6">
              <div className="font-mono-tech text-xs uppercase tracking-[0.26em] text-[#3B82F6]">{c.why.sourcesLabel}</div>
              <ul className="mt-4 flex flex-col gap-1.5">
                {c.why.sources.map((source) => (
                  <li key={source.url}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-11 items-center gap-2 text-sm text-[#93C5FD] underline underline-offset-4 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#3B82F6] rounded-sm"
                    >
                      <ExternalLink size={14} /> {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </section>

        <section data-testid="services-faq" aria-labelledby="services-faq-heading" className="max-w-5xl mx-auto px-6 md:px-10 py-14">
          <motion.div {...fade}>
            <SectionEyebrow>FAQ</SectionEyebrow>
            <h2 id="services-faq-heading" className="mt-4 font-display text-3xl md:text-5xl font-extrabold tracking-tight text-white">{lang === "de" ? "Häufige Fragen zu den Leistungen" : "Frequently asked questions about the services"}</h2>
            <div className="mt-8 space-y-4">
              {c.faq.map((item, index) => (
                <details key={item.q} data-testid={`services-faq-${index + 1}`} className="group rounded-sm border border-white/10 bg-[#07111F] p-5">
                  <summary className="cursor-pointer list-none text-base md:text-lg font-semibold text-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]">{item.q}</summary>
                  <p className="mt-4 text-base leading-relaxed text-gray-400">{item.a}</p>
                  {item.links && (
                    <ul className="mt-4 flex flex-col gap-1.5">
                      {item.links.map((link) => (
                        <li key={link.url}>
                          <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2 text-sm text-[#93C5FD] underline underline-offset-4 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#3B82F6] rounded-sm"><ExternalLink size={14} /> {link.label}</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </details>
              ))}
            </div>
          </motion.div>
        </section>

        <section data-testid="services-final-cta" aria-labelledby="services-final-heading" className="max-w-7xl mx-auto px-6 md:px-10 pb-20">
          <motion.div {...fade} className="relative overflow-hidden rounded-sm border border-[#0052FF]/35 bg-[#07111F] p-6 md:p-10">
            <div className="absolute -top-28 -right-28 h-72 w-72 rounded-full bg-[#0052FF]/18 blur-3xl" />
            <div className="relative">
              <ShieldCheck size={24} className="text-[#3B82F6]" />
              <h2 id="services-final-heading" className="mt-5 max-w-3xl font-display text-3xl md:text-5xl font-extrabold tracking-tight text-white">{c.final.title}</h2>
              <p className="mt-5 max-w-3xl text-lg leading-relaxed text-gray-300">{c.final.body}</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <ActionLink href={BOOKING_URL} external testId="services-final-sprint">{c.final.primary}</ActionLink>
                <ActionLink href={CONTACT_HREF} variant="secondary" testId="services-final-entry">{c.final.secondary}</ActionLink>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-6 text-sm">
                <a
                  href={`mailto:${c.final.contact.email}`}
                  data-testid="services-contact-email"
                  aria-label={lang === "de" ? `E-Mail an ${c.final.contact.email}` : `Email ${c.final.contact.email}`}
                  className="inline-flex min-h-11 items-center gap-2 text-[#93C5FD] underline underline-offset-4 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#3B82F6] rounded-sm"
                >
                  <Mail size={15} className="text-[#3B82F6]" />{c.final.contact.email}
                </a>
                <a
                  href={`tel:${c.final.contact.phoneHref}`}
                  data-testid="services-contact-phone"
                  aria-label={lang === "de" ? `Anrufen: ${c.final.contact.phone}` : `Call ${c.final.contact.phone}`}
                  className="inline-flex min-h-11 items-center gap-2 text-[#93C5FD] underline underline-offset-4 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#3B82F6] rounded-sm"
                >
                  <Phone size={15} className="text-[#3B82F6]" />{c.final.contact.phone}
                </a>
              </div>
            </div>
          </motion.div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
